import { List, Map as ImmutableMap, Range } from 'immutable';
import I18n from 'I18n';
import { DEFAULT_EVENT_DURATION, ONE_DAY_IN_MILLISECONDS } from '../constants/time';
import { add, compare, diff, getCachedMoment, getDayOfWeek, getDisplayMonthRows, getDisplayStartDate, getHour, getWeekLowerBound, getWeekUpperBound, isAfter, isBefore, isSame, isSunday, isWeekday, momentWithTimezone } from './time';
import { MAX_MULTI_DAY_EVENTS_PER_DAY } from '../constants/events';
/**
 * Gets lower and upper bounds from day of the year and year, based on the timezone
 * and unit of time (defaults to 'month')
 */
export function getBoundsFromCalendarProps({
  displayDay,
  displayYear,
  timezone,
  unitOfTime = 'month'
}) {
  const momentInput = `${displayYear}${`${displayDay}`.padStart(3, '0')}`;
  return {
    start: momentWithTimezone(timezone, momentInput).startOf(unitOfTime),
    end: momentWithTimezone(timezone, momentInput).endOf(unitOfTime)
  };
}
/**
 * Takes the day and the start and end of an event and determines if the event should be displayed on the day.
 * It also returns additional information about the event and the day like if the event is in the future.
 */
export function shouldDisplayOnDay({
  day,
  startOfEvent,
  endOfEvent
}) {
  const startOfDay = day.valueOf();
  const endOfDay = startOfDay + ONE_DAY_IN_MILLISECONDS - 1;
  const eventStartsInsideTheDay = startOfDay <= startOfEvent && startOfEvent <= endOfDay;
  const eventEndsInsideTheDay = startOfDay <= endOfEvent && endOfEvent <= endOfDay;
  const dayIsInsideTheEvent = startOfEvent <= startOfDay && endOfDay <= endOfEvent;
  const eventIsInTheFuture = startOfEvent > endOfDay;
  return {
    shouldDisplay: eventStartsInsideTheDay || eventEndsInsideTheDay || dayIsInsideTheEvent,
    eventStartsInsideTheDay,
    eventEndsInsideTheDay,
    dayIsInsideTheEvent,
    eventIsInTheFuture
  };
}
/**
 * Buckets the events in each day.
 * Given that the events are already chronologically sorted and from the longest
 * to the shortest, including all day events, it traverses the list grouping events into days.
 *
 * - It treats the list of events as a queue
 * - It goes through the period specified day by day
 * - If the event starts on the day, it adds it to the day group
 * - If the event starts in the future, we stop taking events for the day
 * - If we encounter an all day event, we add it to the day group and increment the index to the next event
 * - The all day event stays in the queue so it can be picked up by subsequent days
 */
export function getEventsGroupedByDay({
  sortedChildren = [],
  startOfPeriod,
  endOfPeriod
}) {
  const remainingEvents = [...sortedChildren];
  const result = [];
  const day = startOfPeriod.clone();
  while (day.isBefore(endOfPeriod) && remainingEvents.length > 0) {
    const dayGroup = [];
    let i = 0;
    while (i < remainingEvents.length) {
      const child = remainingEvents[i];
      const {
        start: startOfEvent,
        end: endOfEvent
      } = child.props;
      const {
        shouldDisplay,
        eventEndsInsideTheDay,
        eventIsInTheFuture
      } = shouldDisplayOnDay({
        day,
        startOfEvent,
        endOfEvent
      });
      if (eventIsInTheFuture) {
        break;
      }
      if (shouldDisplay) {
        dayGroup.push(child);
      }
      if (eventEndsInsideTheDay) {
        remainingEvents.splice(i, 1);
      } else {
        i++;
      }
    }
    if (dayGroup.length > 0) {
      result.push({
        day: day.clone(),
        children: dayGroup
      });
    }
    day.add(1, 'day');
  }
  return result;
}
/**
 * Filters out weekends day groups if hideWeekends is true
 */
export function filterEventsByWeekday({
  eventsGroupedByDays,
  hideWeekends
}) {
  return eventsGroupedByDays.filter(dayGroup => !hideWeekends || isWeekday(dayGroup.day.day()));
}
export function isAllDayEvent(event) {
  if (!event.endDate) {
    return false;
  }
  return diff(event.endDate, event.eventDate, 'hours') >= 24;
}

/**
 * Same as getSortedChildren exported by ui-addon-calendars/lib/eventParse.
 * However, this method takes into account actual calendar events as opposed to React child components.
 */
export function sortCalendarEvents({
  events,
  timezone,
  useExactEventTimes
}) {
  return events.sort((eventA, eventB) => {
    const startA = eventA.eventDate;
    const endA = eventA.endDate || startA + DEFAULT_EVENT_DURATION;
    const aAllDay = eventA._isAllDayEvent;
    const startB = eventB.eventDate;
    const endB = eventB.endDate || startB + DEFAULT_EVENT_DURATION;
    const bAllDay = eventB._isAllDayEvent;
    if (!(aAllDay && bAllDay) && (aAllDay || bAllDay) && isSame(startA, startB, timezone, 'day')) {
      return aAllDay ? -1 : 1;
    }
    let eventALength;
    let eventBLength;
    if (aAllDay && bAllDay) {
      eventALength = diff(endA, startA, 'days') + 1;
      eventBLength = diff(endB, startB, 'days') + 1;
    } else {
      eventALength = diff(endA, startA, 'hours');
      eventBLength = diff(endB, startB, 'hours');
    }
    if (!isSame(startA, startB, timezone, 'day')) {
      return compare(startA, startB, timezone, 'day');
    }
    if (eventALength !== eventBLength) {
      if (eventALength > eventBLength) return -1;
      if (eventALength < eventBLength) return 1;
    }
    return compare(startA, startB, timezone, 'minute');
  }).map(event => {
    const start = event.eventDate;
    const end = event.endDate || start + DEFAULT_EVENT_DURATION;
    return event.asImmutable().set('_isAllDayEvent', isAllDayEvent(event)).set('_eventLength', diff(end, start, 'hour', useExactEventTimes) || 1);
  }).toList();
}
function getEventIndex(events) {
  const index = events.findIndex(event => {
    return !event;
  });
  return index !== -1 ? index : events.size;
}

/**
 * Same as `distributeEventsToWeekDays` exported by `ui-addon-calendars/lib/eventParse`.
 * However, this method takes into account actual calendar events as opposed to React child components.
 */
export function distributeEventsToWeekDays({
  events,
  hideWeekends,
  timezone,
  weekEndDate,
  weekStartDate
}) {
  const doesWeekStartOnSunday = isSunday(weekStartDate);
  return events.reduce((accumulator, event) => {
    const start = event.eventDate;
    const endRaw = event.endDate || start + DEFAULT_EVENT_DURATION;
    const allDay = event._isAllDayEvent;

    // ensure single-day events don't render on next day (e.g. 1hr event starting at 23:30)
    const end = allDay ? endRaw : start;
    if (!start || isBefore(end, weekStartDate, timezone) || isAfter(start, weekEndDate, timezone)) {
      return accumulator;
    }
    const visibleStart = isBefore(start, weekStartDate, timezone) ? weekStartDate : start;
    const visibleEnd = isBefore(end, weekEndDate, timezone) ? end : weekEndDate;
    const eventStartWeekDay = getDayOfWeek(visibleStart, timezone);
    let eventsForWeekDay = accumulator.get(String(eventStartWeekDay));
    const eventDuration = diff(getCachedMoment(visibleEnd, timezone), getCachedMoment(visibleStart, timezone), 'days') + 1;
    const rootEventIndex = getEventIndex(eventsForWeekDay);
    const clonedEvent = event.asImmutable().set('_eventLength', eventDuration);
    if (eventDuration > 1) {
      const lastDayOfEventThisWeek = eventStartWeekDay + eventDuration;
      Range(eventStartWeekDay + 1, lastDayOfEventThisWeek).forEach((placeholderDayOfWeek, index) => {
        const shouldAddPlaceholder = !(index === 0 && doesWeekStartOnSunday && hideWeekends);
        const eventsForWeek = accumulator.get(String(placeholderDayOfWeek));
        const placeholderEvent = event.asImmutable().set('_isPlaceholderEvent', shouldAddPlaceholder);
        if (eventsForWeek.size <= rootEventIndex) {
          accumulator = accumulator.set(String(placeholderDayOfWeek), eventsForWeek.set(rootEventIndex, placeholderEvent));
        } else {
          accumulator = accumulator.set(String(placeholderDayOfWeek), eventsForWeek.insert(rootEventIndex, placeholderEvent));
        }
      });
    }
    eventsForWeekDay = accumulator.get(String(eventStartWeekDay));
    if (eventsForWeekDay.size > 0 && !eventsForWeekDay.get(rootEventIndex)) {
      accumulator = accumulator.set(String(eventStartWeekDay), eventsForWeekDay.set(rootEventIndex, clonedEvent));
    } else {
      accumulator = accumulator.set(String(eventStartWeekDay), eventsForWeekDay.insert(rootEventIndex, clonedEvent));
    }
    return accumulator;
  }, ImmutableMap({
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  }));
}

/**
 * Same as getOverflowsAndEventKeysForWeek exported by ui-addon-calendars/lib/eventParse.
 * However, this method takes into account actual calendar events as opposed to React child components.
 */
export function getOverflowsAndEventKeysForWeek({
  eventMap,
  maxEventDisplayCount
}) {
  const result = eventMap.keySeq().reduce((acc, dayIndex) => {
    const dayEvents = eventMap.get(dayIndex);
    const overflowCount = dayEvents.size > maxEventDisplayCount ? Math.max(dayEvents.size + 1 - maxEventDisplayCount, 0) : 0;
    const multiDayEvents = [];
    const singleDayEvents = [];
    dayEvents.forEach(event => {
      if (event._eventLength != null && event._eventLength > 1) {
        multiDayEvents.push(event.id);
      } else {
        singleDayEvents.push(event.id);
      }
    });
    acc[dayIndex] = {
      overflowCount,
      multiDayEvents,
      singleDayEvents
    };
    return acc;
  }, {});
  return result;
}

/**
 * Re-orders the events to keep only the max multi-day events visible
 * Loops through the events for the week and re-orders the events List
 * sending multi-day events to the end based on:
 * 1. The current day has an overflowCount
 * 2. The multi-day event's index is greater than the maxMultiDayEventDisplayCount
 */
export function getCorrectOrderOfEventsWithMultiDay({
  eventMap,
  maxEventDisplayCount,
  maxMultiDayEventDisplayCount
}) {
  const overflowsAndEventKeysForWeek = getOverflowsAndEventKeysForWeek({
    eventMap,
    maxEventDisplayCount
  });
  const multiDayEventsToOverflow = {};
  return eventMap.keySeq().reduce((acc, dayIndex) => {
    const dayEvents = eventMap.get(dayIndex);
    const {
      overflowCount,
      multiDayEvents
    } = overflowsAndEventKeysForWeek[dayIndex];
    if (!multiDayEvents.length) {
      acc = acc.set(dayIndex, dayEvents);
      return acc;
    }
    let multiDayEventsList = List();
    let validDayEvents = List();
    dayEvents.forEach((event, index) => {
      const eventLength = event._eventLength != null ? event._eventLength : 0;
      const isMultiEventOverMaxLimit = index + 1 > maxMultiDayEventDisplayCount && eventLength > 1;
      if (overflowCount && isMultiEventOverMaxLimit) {
        multiDayEventsToOverflow[event.id] = true;
      }
      if (multiDayEventsToOverflow[event.id]) {
        const finalEvent = event.asImmutable().set('_multiDayEventHidden', true);
        multiDayEventsList = multiDayEventsList.push(finalEvent);
      } else {
        validDayEvents = validDayEvents.push(event);
      }
    });
    acc = acc.set(dayIndex, validDayEvents.concat(multiDayEventsList).toList());
    return acc;
  }, ImmutableMap({
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  }));
}

/**
 * Adds multiDayEventHidden to multi-day events based on next days events.
 * Loops through the events and the next days of the week and checks if
 * multi-day events should be sent to the overflow as they're not able
 * to show their entire length for the week based on:
 * 1. Having a single-day event on the following days
 * 2. Having a overflow of multi-day events on the following days and their index being equal to the maxEventDisplayCount
 */
export function getEventsTaggedWithHiddenMultiDayEvents({
  eventMap,
  maxEventDisplayCount,
  maxMultiDayEventDisplayCount
}) {
  const overflowsAndEventKeysForWeek = getOverflowsAndEventKeysForWeek({
    eventMap,
    maxEventDisplayCount
  });
  return eventMap.keySeq().reduce((acc, dayIndex) => {
    const dayEventsRaw = eventMap.get(dayIndex);
    let dayEvents = dayEventsRaw;
    dayEvents.forEach((event, index) => {
      const eventLength = event._eventLength != null ? event._eventLength : 0;
      const isMultiEventOverMaxLimit = index + 1 > maxMultiDayEventDisplayCount && eventLength > 1;
      if (isMultiEventOverMaxLimit) {
        for (let dayLeft = Number(dayIndex) + 1; dayLeft < 7; dayLeft++) {
          const {
            overflowCount: dayLeftOverflowCount,
            multiDayEvents: dayLeftMultiDayEvents,
            singleDayEvents: dayLeftSingleDayEvents
          } = overflowsAndEventKeysForWeek[dayLeft];
          if (!event._isPlaceholderEvent && dayLeftOverflowCount && (dayLeftSingleDayEvents.length || dayLeftMultiDayEvents.length > maxEventDisplayCount && index + 1 === maxEventDisplayCount) && dayLeftMultiDayEvents.includes(event.id)) {
            const finalEvent = event.asImmutable().set('_multiDayEventHidden', true);
            dayEvents = dayEvents.set(index, finalEvent);
            break;
          }
        }
      }
    });
    acc = acc.set(dayIndex, dayEvents);
    return acc;
  }, ImmutableMap({
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  }));
}
export function getEventMapForDay({
  events,
  maxEventDisplayCount
}) {
  let hasOverflow = events.size > maxEventDisplayCount;
  const eventsToDisplay = events.reduce((accumulator, event, index) => {
    const isMultiDayEventHidden = event && event._multiDayEventHidden;
    if (isMultiDayEventHidden) {
      hasOverflow = true;
    }
    const shouldBeInOverflow = hasOverflow && index + 1 >= maxEventDisplayCount || isMultiDayEventHidden;
    if (!event || shouldBeInOverflow) {
      return accumulator;
    }
    accumulator = accumulator.push({
      event,
      displayIndex: index
    });
    return accumulator;
  }, List());
  const allNonPlaceholderEvents = events.filter(Boolean);
  const overflowCount = allNonPlaceholderEvents.size - eventsToDisplay.size;
  return {
    displayEvents: eventsToDisplay,
    events: allNonPlaceholderEvents,
    overflowCount,
    hasOverflow
  };
}
export function getNoUndefinedEvents(eventMap) {
  return eventMap.keySeq().reduce((acc, dayIndex) => {
    const dayEventsRaw = eventMap.get(dayIndex);
    acc = acc.set(dayIndex, dayEventsRaw.filter(Boolean).toList());
    return acc;
  }, ImmutableMap({
    0: List(),
    1: List(),
    2: List(),
    3: List(),
    4: List(),
    5: List(),
    6: List()
  }));
}
export function getEventMapForWeek({
  displayMonth,
  displayYear,
  events,
  maxEventDisplayCount,
  timezone,
  week
}) {
  const startCutoff = getDisplayStartDate(displayYear, displayMonth, timezone);
  const weekStartDayRaw = add(startCutoff, 7 * week, 'days');
  const weekStartDay = timezone ? weekStartDayRaw.tz(timezone).startOf('day') : weekStartDayRaw.startOf('day');
  const weekEndDayRaw = add(startCutoff, 7 * (week + 1) - 1, 'days');
  const weekEndDay = timezone ? weekEndDayRaw.tz(timezone).endOf('day') : weekEndDayRaw.endOf('day');
  const eventsRaw = distributeEventsToWeekDays({
    events,
    timezone,
    weekEndDate: weekEndDay,
    weekStartDate: weekStartDay
  });
  const validEvents = getNoUndefinedEvents(eventsRaw);
  const sortedEvents = getCorrectOrderOfEventsWithMultiDay({
    eventMap: validEvents,
    maxEventDisplayCount,
    maxMultiDayEventDisplayCount: MAX_MULTI_DAY_EVENTS_PER_DAY
  });
  const finalEvents = getEventsTaggedWithHiddenMultiDayEvents({
    eventMap: sortedEvents,
    maxEventDisplayCount,
    maxMultiDayEventDisplayCount: MAX_MULTI_DAY_EVENTS_PER_DAY
  });
  return Range(0, 7).map(index => getEventMapForDay({
    events: finalEvents.get(String(index)),
    maxEventDisplayCount
  })).toList();
}
export function distributeEventsToHours(events, timezone) {
  const hourDistribution = Range(0, 24).map(() => List()).toList();
  return events.reduce((accumulator, event) => {
    const start = event.eventDate;
    const end = event.endDate || start + DEFAULT_EVENT_DURATION;
    let eventDuration = diff(end, start, 'hours');

    /**
     * Note 2024-03-27
     *  - this logic could be wrong as pointed out in https://git.hubteam.com/HubSpot/ui-addon-calendars/pull/522#discussion_r3057037 and https://git.hubteam.com/HubSpot/ui-addon-calendars/pull/522#discussion_r3057112
     *
     * We should revisit this logic once we get rid of all JS implementation in eventMap.js and eventParse.js files.
     */
    // we want any events < 1 hour to default to 1 hour length
    eventDuration = eventDuration > 1 ? 1 : eventDuration;
    const startHour = getHour(start, timezone);
    const updatedListForHour = accumulator.get(startHour).push(event);
    accumulator = accumulator.set(startHour, updatedListForHour);
    if (eventDuration > 1) {
      Range(1, eventDuration).forEach(hour => {
        const placeholderEvent = event.asImmutable().set('_isPlaceholderEvent', true);
        const listForHour = accumulator.get(startHour + hour).push(placeholderEvent);
        accumulator = accumulator.set(startHour + hour, listForHour);
      });
    }
    return accumulator;
  }, hourDistribution);
}

/**
 * Group events by week (row) and days of week with list of events when in MONTH view.
 *
 * | Week Number | Mon | Tue | Wed | Thu | Fri | Sat | Sun |
 * |-------------|-----|-----|-----|-----|-----|-----|-----|
 * | 1           |     |     |     |     |     |     |     |
 * | 2           |     |     |     |     |     |     |     |
 * | 3           |     |     |     |     |     |     |     |
 * | 4           |     |     |     |     |     |     |     |
 * | 5           |     |     |     |     |     |     |     |
 *
 * {
 *  0: [<array with 7 positions, each item represents one day of the week>],
 *  1: [<array with 7 positions, each item represents one day of the week>],
 *  2: [<array with 7 positions, each item represents one day of the week>],
 *  3: [<array with 7 positions, each item represents one day of the week>],
 *  4: [<array with 7 positions, each item represents one day of the week>],
 * }
 */
export function groupEventsByWeekRowAndDayOfWeek({
  displayMonth,
  displayYear,
  events,
  maxEventDisplayCount,
  timezone
}) {
  const sortedEvents = sortCalendarEvents({
    events,
    timezone,
    useExactEventTimes: false
  });
  const displayMonthRows = getDisplayMonthRows(displayYear, displayMonth);
  return Range(0, displayMonthRows).map(week => {
    return getEventMapForWeek({
      week,
      events: sortedEvents,
      displayYear,
      displayMonth,
      maxEventDisplayCount,
      timezone
    });
  }).toList();
}

/**
 * Group events by day of week and hours with list of events when in WEEK view.
 *
 * | Mon | Tue | Wed | Thu | Fri | Sat | Sun |
 * |-----|-----|-----|-----|-----|-----|-----|
 * |     |     |     |     |     |     |     |
 * |     |     |     |     |     |     |     |
 * |     |     |     |     |     |     |     |
 * |     |     |     |     |     |     |     |
 * |     |     |     |     |     |     |     |
 *
 * {
 *  0: [<array with 24 positions, each item represents one hour of the day>],
 *  1: [<array with 24 positions, each item represents one hour of the day>],
 *  2: [<array with 24 positions, each item represents one hour of the day>],
 *  3: [<array with 24 positions, each item represents one hour of the day>],
 *  4: [<array with 24 positions, each item represents one hour of the day>],
 *  5: [<array with 24 positions, each item represents one hour of the day>],
 *  6: [<array with 24 positions, each item represents one hour of the day>],
 * }
 */
export function groupEventsByDayOfWeekAndHours({
  displayDay,
  displayYear,
  events,
  hideWeekends,
  timezone,
  useExactEventTimes
}) {
  const sortedEvents = sortCalendarEvents({
    events,
    timezone,
    useExactEventTimes
  });
  const weekStartDate = getWeekLowerBound(displayYear, displayDay, hideWeekends, timezone, I18n.moment.locale());
  const weekEndDate = getWeekUpperBound(displayYear, displayDay, hideWeekends, timezone, I18n.moment.locale());
  const eventsForWeek = sortedEvents.filter(event => {
    const start = event.eventDate;
    const end = event.endDate || start + DEFAULT_EVENT_DURATION;
    return !isBefore(end, weekStartDate, timezone) && !isAfter(start, weekEndDate, timezone);
  }).toList();
  const nonAllDayEvents = eventsForWeek.filter(event => !event._isAllDayEvent).toList();

  // distribute by day and hour
  return Range(0, 7).map(dayOfWeek => {
    return distributeEventsToHours(nonAllDayEvents.filter(event => {
      const start = event.eventDate;
      return getDayOfWeek(start, timezone) === dayOfWeek;
    }).toList(), timezone);
  }).toList();
}